import { EUGENE_WALLET_ADDRESS, LeaderboardRecordCategory, SECONDS_IN_DAY } from '@/constants/shared';
import { useAuth } from '@/hooks/useAuth';
import { LeaderboardRecord, SeasonOneAllocation } from '@/types';
import { formatCurrencyToLocale, formatNumberToLocale, truncateAddress } from '@/utils';
import { Box, Button, HStack, Image, Table, TableCellProps, Text } from '@chakra-ui/react';
import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { GoonAvatar } from './goon-avatar';
import { Rank } from './rank';
import { TableSkeleton } from './ui/skeletons/table-skeleton';
import doveColoredPathSvg from '/assets/dove-color.svg';
import xpPathSvg from '/assets/leaderboard/pp.svg';
import eugenePfp from '/assets/profile/eugene.png';

const StyledTableCell = ({ children, ...rest }: { children: ReactNode } & TableCellProps) => (
  <Table.Cell borderColor="bg.subtle" py={6} {...rest}>
    {children}
  </Table.Cell>
);

const StyledColumnHeader = ({ children, ...rest }: { children: ReactNode } & TableCellProps) => (
  <Table.ColumnHeader color="text.secondary" borderColor="bg.subtle" pb={0} fontSize="sm" {...rest}>
    {children}
  </Table.ColumnHeader>
);

const recordTypeToRank: {
  leaderboard: 'xpRank';
  battleGroup: 'bgRank';
} = {
  leaderboard: 'xpRank',
  battleGroup: 'bgRank',
};

export const LeaderboardTable = ({
  leaderboard,
  recordType,
  fetchNextPage,
  hasNextPage,
  userOverride,
  currentUserData, // pass in the "currentUserData" from the parent component so it can be overridden by admin viewing
  isLoading, // currentUserData isLoading
}: {
  leaderboard: LeaderboardRecord[] | undefined;
  recordType: LeaderboardRecordCategory;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  userOverride?: {
    userRank: number;
    userXp: number;
  };
  currentUserData: { stats: LeaderboardRecord; seasonOneAllocation: SeasonOneAllocation } | undefined;
  isLoading: boolean;
}) => {
  const { address } = useAuth();
  const navigate = useNavigate();

  const { stats: currentUserStats } = currentUserData ?? {};

  const accentBgColor = recordType === LeaderboardRecordCategory.Leaderboard ? 'bg.accent3' : 'bg.accent2';

  const tableData = useMemo(() => {
    if (!leaderboard) return [];
    if (!currentUserStats) return leaderboard;
    return [
      {
        ...currentUserStats,
        // if there's a user override for DAY 1 battle group leaderboard, use that instead of the leaderboard data
        ...(userOverride ? { bgRank: userOverride.userRank, totalXp: userOverride.userXp } : {}),
      },
      ...leaderboard,
    ];
  }, [leaderboard, currentUserStats, userOverride]);

  // add a placeholder for Eugene on the leaderboard until one week after launch
  const finalTableData = useMemo(() => {
    const oneWeekPastLaunchTimestamp =
      (Number(import.meta.env.VITE_DAY_ONE_TIMESTAMP || '0') + SECONDS_IN_DAY * 7) * 1000;
    if (
      recordType === LeaderboardRecordCategory.Leaderboard &&
      new Date().getTime() < oneWeekPastLaunchTimestamp &&
      tableData &&
      tableData.length > 0
    ) {
      // Create a new array instead of mutating the original
      return [
        ...(address ? [tableData[0]] : []), // Only include user data if logged in
        {
          walletAddress: EUGENE_WALLET_ADDRESS,
        },
        ...(address ? tableData.slice(1) : tableData), // Add the rest of the items or the full table if not logged in
      ] as LeaderboardRecord[];
    }
    return tableData;
  }, [tableData, recordType]);

  const rankToUseForRecordType = recordTypeToRank[recordType];

  return (
    <Box width="full" overflowX="auto" mt={4}>
      {!finalTableData?.length || isLoading ? (
        <TableSkeleton mt={16} />
      ) : (
        <>
          <Table.ScrollArea>
            <Table.Root
              borderCollapse="separate"
              borderSpacing={'0 10px'}
              fontWeight="500"
              fontFamily="Inter, sans-serif"
              fontSize="md"
            >
              <Table.Header>
                <Table.Row fontSize="sm" bg="transparent">
                  <StyledColumnHeader>Position</StyledColumnHeader>
                  <StyledColumnHeader>User</StyledColumnHeader>
                  <StyledColumnHeader hideBelow={'md'}>Plume Staking | Streak</StyledColumnHeader>
                  <StyledColumnHeader hideBelow={'md'}>TVL | Longest Daily Streak</StyledColumnHeader>
                  <StyledColumnHeader hideBelow={'md'}>Referrals</StyledColumnHeader>
                  <StyledColumnHeader textAlign="right" pr={6}>
                    Plume Points
                  </StyledColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {finalTableData.map((row, index) => {
                  const isUserCell = row.walletAddress === currentUserStats?.walletAddress && index === 0;
                  const targetUrl = isUserCell ? 'me' : row.walletAddress;
                  const isEugene = row.walletAddress === EUGENE_WALLET_ADDRESS;
                  return (
                    <Table.Row
                      key={isUserCell ? 'me' : row.walletAddress}
                      className={!isUserCell ? 'table-row' : ''}
                      onClick={() => navigate(`/profile/${targetUrl}`)}
                      _hover={{ cursor: 'pointer' }}
                      backgroundColor={isUserCell ? accentBgColor : 'bg'}
                    >
                      <StyledTableCell
                        pl={6}
                        borderTopLeftRadius="xl"
                        borderBottomLeftRadius="xl"
                        border="1px solid #f0f0f0"
                        borderRight="none"
                        pr={[4, null, 'inherit']}
                      >
                        {isEugene ? (
                          <Image src={doveColoredPathSvg} boxSize={6} />
                        ) : (
                          <Rank rank={row[rankToUseForRecordType]} />
                        )}
                      </StyledTableCell>
                      <StyledTableCell border="1px solid #f0f0f0" borderRight="none" borderLeft="none">
                        <HStack>
                          {isEugene ? (
                            <Image src={eugenePfp} boxSize={8} rounded="full" />
                          ) : (
                            <GoonAvatar seed={row.walletAddress} boxSize={8} />
                          )}

                          {isEugene
                            ? 'Eugene Y. Q. Shen'
                            : isUserCell
                              ? 'You'
                              : truncateAddress(row.walletAddress, true)}
                        </HStack>
                      </StyledTableCell>
                      <StyledTableCell border="1px solid #f0f0f0" borderRight="none" borderLeft="none" hideBelow={'md'}>
                        {isEugene ? (
                          '∞'
                        ) : (
                          <>
                            {formatNumberToLocale(row.currentPlumeStakingTotalTokens || 0)} |{' '}
                            {row.plumeStakingLongestStreakDays || 0}
                          </>
                        )}
                      </StyledTableCell>
                      <StyledTableCell border="1px solid #f0f0f0" borderRight="none" borderLeft="none" hideBelow={'md'}>
                        {isEugene ? (
                          '∞'
                        ) : (
                          <>
                            {formatCurrencyToLocale(row.TVL || 0)} | {row.longestTvlStreak || 0}
                          </>
                        )}
                      </StyledTableCell>
                      <StyledTableCell border="1px solid #f0f0f0" borderRight="none" borderLeft="none" hideBelow={'md'}>
                        {isEugene ? '∞' : <>{row.referralCount || 0}</>}
                      </StyledTableCell>
                      <StyledTableCell
                        border="1px solid #f0f0f0"
                        borderLeft="none"
                        borderTopRightRadius="xl"
                        borderBottomRightRadius="xl"
                      >
                        {isEugene ? null : (
                          <HStack gap={3} justifyContent="end" pr={4}>
                            <Image src={xpPathSvg} mr={-1} boxSize={5} hideBelow={'md'} />
                            <Text>{formatNumberToLocale(row.totalXp ?? 0)}</Text>
                          </HStack>
                        )}
                      </StyledTableCell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
          {hasNextPage && (
            <Box textAlign="center" mt={4}>
              <Button variant="outline" size="md" onClick={fetchNextPage}>
                Load More
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
